// SPDX-License-Identifier: BUSL-1.1

/**
 * RunnerHealthPanel — shows version compliance and label audit per runner class.
 *
 * Fetches:
 * - GET /api/tenant/runner-classes/health (version compliance per class)
 * - GET /api/tenant/runner-classes/labels/audit (forbidden/ambiguous labels per class)
 *
 * Displayed as a tab within the Runners page (TenantRunnerPoolPage).
 */

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import type { RunnerClassHealth, LabelAuditEntry } from '@/types/runner-class';

/* ── API response types ──────────────────────────────────────── */

interface RunnerHealthResponse {
  health: RunnerClassHealth[];
  total: number;
}

interface LabelAuditResponse {
  audit: LabelAuditEntry[];
  total: number;
  hasIssues: boolean;
}

/* ── Version compliance badge ─────────────────────────────────── */

function VersionBadge({ status }: { status: RunnerClassHealth['status'] }) {
  const config: Record<RunnerClassHealth['status'], { bg: string; text: string; dot: string; label: string }> = {
    healthy: { bg: 'bg-green-950/40', text: 'text-green-400', dot: 'bg-green-400', label: 'COMPLIANT' },
    degraded: { bg: 'bg-red-950/40', text: 'text-red-400', dot: 'bg-red-400', label: 'OUTDATED' },
    unknown: { bg: 'bg-neutral-800', text: 'text-neutral-500', dot: 'bg-neutral-500', label: 'UNKNOWN' },
  };
  const c = config[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full ${c.bg} px-2 py-0.5 text-xs font-semibold ${c.text}`}>
      <span className={`size-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  );
}

/* ── Label audit pills ───────────────────────────────────────── */

function ForbiddenPill({ label }: { label: string }) {
  return (
    <span className="inline-flex rounded bg-red-950/40 px-1.5 py-0.5 font-mono text-xs text-red-400">
      {label}
    </span>
  );
}

function AmbiguousPill({ label }: { label: string }) {
  return (
    <span className="inline-flex rounded bg-amber-950/40 px-1.5 py-0.5 font-mono text-xs text-amber-400">
      {label}
    </span>
  );
}

function ValidPill({ label }: { label: string }) {
  return (
    <span className="inline-flex rounded bg-neutral-800 px-1.5 py-0.5 font-mono text-xs text-neutral-400">
      {label}
    </span>
  );
}

/* ── Audit section per class ──────────────────────────────────── */

function LabelAuditSection({ entry }: { entry: LabelAuditEntry }) {
  const hasIssues = entry.forbidden.length > 0 || entry.ambiguous.length > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm text-neutral-200">{entry.className}</span>
        {!hasIssues && (
          <span className="text-xs text-green-400">No issues</span>
        )}
      </div>

      {entry.forbidden.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-red-400">Forbidden labels:</span>
          <div className="flex flex-wrap gap-1">
            {entry.forbidden.map((label) => (
              <ForbiddenPill key={label} label={label} />
            ))}
          </div>
        </div>
      )}

      {entry.ambiguous.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-amber-400">Ambiguous labels:</span>
          <div className="flex flex-wrap gap-1">
            {entry.ambiguous.map((label) => (
              <AmbiguousPill key={label} label={label} />
            ))}
          </div>
        </div>
      )}

      {entry.valid.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neutral-500">Valid labels:</span>
          <div className="flex flex-wrap gap-1">
            {entry.valid.map((label) => (
              <ValidPill key={label} label={label} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main panel ───────────────────────────────────────────────── */

export function RunnerHealthPanel() {
  const { data: healthData, isLoading: healthLoading, error: healthError } = useQuery({
    queryKey: ['tenant-runner-classes-health'],
    queryFn: () => apiFetch<RunnerHealthResponse>('/tenant/runner-classes/health'),
  });

  const { data: auditData, isLoading: auditLoading, error: auditError } = useQuery({
    queryKey: ['tenant-runner-classes-labels-audit'],
    queryFn: () => apiFetch<LabelAuditResponse>('/tenant/runner-classes/labels/audit'),
  });

  const isLoading = healthLoading || auditLoading;
  const error = healthError ?? auditError;

  if (isLoading) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-900 p-4 text-sm text-neutral-400">
        Loading runner health…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded border border-red-900 bg-red-900/20 p-4 text-sm text-red-300">
        Failed to load runner health: {String(error)}
      </div>
    );
  }

  const healthEntries = healthData?.health ?? [];
  const auditEntries = auditData?.audit ?? [];

  if (healthEntries.length === 0 && auditEntries.length === 0) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-900 p-4 text-sm text-neutral-500">
        No runner classes found. Add a fireactions pool to your VM500 config to get started.
      </div>
    );
  }

  // Build a map for quick audit lookups
  const auditMap = new Map<string, LabelAuditEntry>();
  for (const entry of auditEntries) {
    auditMap.set(entry.classId, entry);
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Version compliance section */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-200">Version Compliance</h3>
          <span className="text-xs text-neutral-500">
            Expected: {healthEntries[0]?.expectedVersion ?? '—'}
          </span>
        </div>

        <div className="overflow-hidden rounded border border-neutral-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-800 bg-neutral-900/50">
                <th className="px-4 py-2 text-left font-medium text-neutral-400">Pool</th>
                <th className="px-4 py-2 text-left font-medium text-neutral-400">Agent Version</th>
                <th className="px-4 py-2 text-left font-medium text-neutral-400">Replicas</th>
                <th className="px-4 py-2 text-left font-medium text-neutral-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {healthEntries.map((entry) => (
                <tr key={entry.classId} className="border-b border-neutral-800/50 last:border-0">
                  <td className="px-4 py-2 font-mono text-neutral-200">{entry.className}</td>
                  <td className="px-4 py-2 text-neutral-300">{entry.agentVersion}</td>
                  <td className="px-4 py-2 text-neutral-300">{entry.replicas}</td>
                  <td className="px-4 py-2">
                    <VersionBadge status={entry.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Label audit section */}
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-200">Label Audit</h3>
          {auditData?.hasIssues && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-950/40 px-2 py-0.5 text-xs font-semibold text-amber-400">
              <span className="size-1.5 rounded-full bg-amber-400" />
              Issues Found
            </span>
          )}
        </div>

        {auditEntries.length === 0 ? (
          <p className="text-sm text-neutral-500">No label audit data available.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {auditEntries.map((entry) => (
              <div key={entry.classId} className="rounded border border-neutral-800 p-3">
                <LabelAuditSection entry={entry} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
